import { useState, useCallback, useMemo } from "react";
import { useGpsPosition, type GpsResult } from "./useGpsPosition";

interface CheckInRecord {
  readonly recordedAt: string;
  readonly verification: string;
}

interface ShiftForCheckIn {
  readonly id: string;
  readonly checkIn: CheckInRecord | null;
  readonly checkOut: CheckInRecord | null;
}

type Upcoming = { readonly phase: "upcoming" };
type Locating = { readonly phase: "locating" };
type Confirming = { readonly phase: "confirming"; readonly distanceMetres: number };
type CheckedIn = { readonly phase: "checked_in"; readonly recordedAt: string };
type CheckingOut = { readonly phase: "checking_out" };
type Completed = {
  readonly phase: "completed";
  readonly checkInAt: string;
  readonly checkOutAt: string;
};
type ErrorState = { readonly phase: "error"; readonly previousPhase: "upcoming" | "checked_in" };

export type CheckInState =
  | Upcoming
  | Locating
  | Confirming
  | CheckedIn
  | CheckingOut
  | Completed
  | ErrorState;

function gpsResultToApiFields(gps: GpsResult) {
  if (gps.status === "available") {
    return {
      latitude: gps.latitude,
      longitude: gps.longitude,
      accuracyMetres: gps.accuracy,
      gpsStatus: "available" as const,
    };
  }
  if (gps.status === "timeout") {
    return { latitude: null, longitude: null, accuracyMetres: null, gpsStatus: "timeout" as const };
  }
  return { latitude: null, longitude: null, accuracyMetres: null, gpsStatus: "denied" as const };
}

function deriveInitialState(shift: ShiftForCheckIn): CheckInState {
  if (shift.checkIn && shift.checkOut) {
    return { phase: "completed", checkInAt: shift.checkIn.recordedAt, checkOutAt: shift.checkOut.recordedAt };
  }
  if (shift.checkIn) {
    return { phase: "checked_in", recordedAt: shift.checkIn.recordedAt };
  }
  return { phase: "upcoming" };
}

export function useCheckIn(shift: ShiftForCheckIn) {
  const [state, setState] = useState<CheckInState>(() => deriveInitialState(shift));
  const { capture } = useGpsPosition();

  const lastGps = useMemo(() => ({ current: null as GpsResult | null }), []);

  const postCheckIn = useCallback(async (
    type: "in" | "out",
    gps: GpsResult,
    confirmed: boolean,
  ): Promise<{ success: boolean; needsConfirmation?: boolean; distanceMetres?: number; recordedAt?: string }> => {
    const gpsFields = gpsResultToApiFields(gps);
    const res = await fetch("/api/check-ins", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        shiftId: shift.id,
        type,
        ...gpsFields,
        confirmed,
      }),
    });
    const body = await res.json();
    if (!res.ok) return { success: false };
    if (body.data?.needsConfirmation) {
      return { success: true, needsConfirmation: true, distanceMetres: body.data.distanceMetres };
    }
    return { success: true, recordedAt: body.data?.checkIn?.recordedAt };
  }, [shift.id]);

  const checkIn = useCallback(async () => {
    setState({ phase: "locating" });

    const gps = await capture();
    lastGps.current = gps;

    const result = await postCheckIn("in", gps, false);

    if (!result.success) {
      setState({ phase: "error", previousPhase: "upcoming" });
      return;
    }

    if (result.needsConfirmation && result.distanceMetres != null) {
      setState({ phase: "confirming", distanceMetres: result.distanceMetres });
      return;
    }

    setState({ phase: "checked_in", recordedAt: result.recordedAt ?? new Date().toISOString() });
  }, [capture, postCheckIn, lastGps]);

  const confirmOverride = useCallback(async () => {
    setState({ phase: "locating" });

    const gps = lastGps.current ?? { status: "denied" as const };
    const result = await postCheckIn("in", gps, true);

    if (!result.success) {
      setState({ phase: "error", previousPhase: "upcoming" });
      return;
    }

    setState({ phase: "checked_in", recordedAt: result.recordedAt ?? new Date().toISOString() });
  }, [postCheckIn, lastGps]);

  const cancelOverride = useCallback(() => {
    setState({ phase: "upcoming" });
  }, []);

  const checkOut = useCallback(async () => {
    const checkedInAt = state.phase === "checked_in" ? state.recordedAt : "";
    setState({ phase: "checking_out" });

    const gps = await capture();
    const result = await postCheckIn("out", gps, true);

    if (!result.success) {
      setState({ phase: "error", previousPhase: "checked_in" });
      return;
    }

    setState({
      phase: "completed",
      checkInAt: checkedInAt,
      checkOutAt: result.recordedAt ?? new Date().toISOString(),
    });
  }, [capture, postCheckIn, state]);

  const retry = useCallback(() => {
    if (state.phase === "error") {
      if (state.previousPhase === "checked_in") {
        setState({ phase: "checked_in", recordedAt: shift.checkIn?.recordedAt ?? "" });
      } else {
        setState({ phase: "upcoming" });
      }
    }
  }, [state, shift.checkIn]);

  return { state, checkIn, confirmOverride, cancelOverride, checkOut, retry };
}
